import axios from 'axios'
import Dialog from 'lib/components/Dialog/Dialog'
import AuthContext from 'lib/context/AuthContext'
import useFocusOnMount from 'lib/hooks/useFocusOnMount'
import useOnInputChange from 'lib/hooks/useOnInputChange'
import { reportError } from 'lib/util/errorHandler'
import isUniqueConstraintError from 'lib/util/isUniqueConstraintError'
import ActionDialog from 'pages/Home/admin/ActionDialog/ActionDialog'
import FormGroup from 'pages/Home/admin/FormGroup/FormGroup'
import Series from 'pages/Home/Series'
import React, { FormEvent, useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export interface AddSeriesDialogProps {
  initialSeries?: Series
  onRequestClose: ()=> void
}

export default function AddSeriesDialog(props: AddSeriesDialogProps) {
  const { initialSeries, onRequestClose } = props
  const [t] = useTranslation()
  const [apiState, setApiState] = useState({ error: null, saving: false })
  const { getAccessToken } = useContext(AuthContext)
  const [state, setState] = useState(initialSeries || { published: false, title: '' })
  const onInputChange = useOnInputChange<Series>(setState)
  const inputEl = useRef(null)
  useFocusOnMount(inputEl)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setApiState({ saving: true, error: null })
    try {
      const accessToken = await getAccessToken()
      const headers = { Authorization: `Bearer ${accessToken}` }
      const body = { published: state.published, title: state.title.trim() }
      if (initialSeries) {
        await axios.put(`${process.env.THREESIXTY_TRAINING_URI}/admin/series/${initialSeries.id}`, body, { headers })
      } else {
        await axios.post(`${process.env.THREESIXTY_TRAINING_URI}/admin/series`, body, { headers })
      }
      window.location.reload()
    } catch (err) {
      if (isUniqueConstraintError(err)) {
        return setApiState({ saving: false, error: t('home.series.admin.addDialog.uniqueError') })
      }
      setApiState({ saving: false, error: t('home.series.admin.addDialog.apiError') })
      reportError(err, `Error ${initialSeries ? 'editing' : 'adding'} series`, {
        seriesId: initialSeries ? initialSeries.id : null
      })
    }
  }

  const { error, saving } = apiState

  return (
    <Dialog className="add-series-dialog" onRequestClose={onRequestClose}>
      <Dialog.Header onRequestClose={onRequestClose}>
        <Dialog.Title>
          {initialSeries ? t('home.series.admin.addDialog.editTitle') : t('home.series.admin.addDialog.title')}
        </Dialog.Title>
      </Dialog.Header>
      <form onSubmit={onSubmit}>
        <ActionDialog.Body>
          <FormGroup.Input
            ref={inputEl}
            onChange={onInputChange}
            name="title"
            placeholder={t('home.series.admin.addDialog.titlePlaceholder')}
            value={state.title}
            minLength={3}
            notBlank
          />
          <h4>{t('home.series.admin.addDialog.makePublic')}</h4>
          <FormGroup.Input
            label={t('home.series.admin.addDialog.publish')}
            onChange={onInputChange}
            name="published"
            placeholder={t('home.series.admin.addDialog.publish')}
            type="checkbox"
            checked={state.published}
          />
        </ActionDialog.Body>
        <ActionDialog.Footer>
          <div>
            <ActionDialog.CancelButton onClick={onRequestClose} />
            <ActionDialog.SaveButton loading={saving} />
          </div>
          <ActionDialog.Error message={error} />
        </ActionDialog.Footer>
      </form>
    </Dialog>
  )
}
